<template>
  <div class="tu-faq">
    <details
      v-for="(item, i) in items"
      :key="i"
      class="tu-faq__item"
      :open="openFirst && i === 0"
      :name="exclusive ? groupName : null"
    >
      <summary class="tu-faq__q">
        <span class="tu-faq__num" aria-hidden="true">{{ i + 1 }}</span>
        <span class="tu-faq__q-text">{{ item.q }}</span>
        <span class="tu-faq__icon" aria-hidden="true">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 9 7 7 7-7"/></svg>
        </span>
      </summary>
      <div class="tu-faq__body">
        <div class="tu-faq__body-inner">
          <p class="tu-faq__a">{{ item.a }}</p>
        </div>
      </div>
    </details>
  </div>
</template>

<script>
let uid = 0

export default {
  name: 'TuFaq',
  props: {
    /** [{ q, a }] */
    items: { type: Array, required: true },
    /** Open the first item by default */
    openFirst: { type: Boolean, default: true },
    /** One-at-a-time accordion via the native details name attribute */
    exclusive: { type: Boolean, default: true }
  },
  data() {
    uid += 1
    return { groupName: `tu-faq-${uid}` }
  }
}
</script>

<style lang="scss" scoped>
.tu-faq {
  display: flex;
  flex-direction: column;
  gap: 0;
  background: var(--tu-surface);
  border: 1px solid var(--tu-line-soft, var(--tu-line));
  border-radius: var(--tu-r-lg, 14px);
  box-shadow: var(--tu-shadow-card, 0 1px 2px rgba(24, 24, 24, 0.04));
  overflow: hidden;
}

.tu-faq__item {
  background: transparent;
  transition: background var(--tu-dur-fast, 0.15s) ease;
}
.tu-faq__item + .tu-faq__item {
  border-top: 1px solid var(--tu-line-soft, var(--tu-line));
}
.tu-faq__item[open] {
  box-shadow: inset 3px 0 0 var(--tu-blue);
}

.tu-faq__q {
  list-style: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 18px 22px;
  user-select: none;
}
.tu-faq__q::-webkit-details-marker { display: none; }
.tu-faq__q:focus-visible {
  outline: 2px solid var(--tu-blue);
  outline-offset: -3px;
  border-radius: 0;
}

/* The number carries the display face, the question stays in the text face, like the path steps. */
.tu-faq__num {
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--tu-cta);
  color: var(--tu-on-cta);
  font-family: var(--tu-font-display);
  font-weight: var(--tu-fw-display, 800);
  font-size: 15px;
  letter-spacing: -0.01em;
}
.tu-faq__q-text {
  flex: 1 1 auto;
  min-width: 0;
  font-family: var(--tu-font-text);
  font-weight: 700;
  font-size: clamp(15.5px, 2vw, 17px);
  letter-spacing: -0.005em;
  line-height: 1.35;
  color: var(--tu-ink);
}

.tu-faq__icon {
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--tu-ink-soft);
  background: var(--tu-surface-2, #f4f3ef);
  transition: transform var(--tu-dur-fast, 0.18s) ease, background var(--tu-dur-fast, 0.18s) ease, color var(--tu-dur-fast, 0.18s) ease;
}
.tu-faq__item[open] .tu-faq__icon {
  transform: rotate(180deg);
  background: var(--tu-blue);
  color: #fff;
}

/* Smooth height animation without JS: grid-rows 0fr -> 1fr */
.tu-faq__body {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.24s ease;
}
.tu-faq__item[open] .tu-faq__body {
  grid-template-rows: 1fr;
}
.tu-faq__body-inner {
  overflow: hidden;
  min-height: 0;
}

.tu-faq__a {
  margin: 0;
  padding: 0 22px 20px;
  font-size: 15.5px;
  line-height: 1.68;
  color: var(--tu-ink-soft);
  max-width: 72ch;
}

@media (prefers-reduced-motion: reduce) {
  .tu-faq__body,
  .tu-faq__icon { transition: none; }
}
</style>
